import { IKey } from '@/common/constants/ikey'
import { resolveClientDataActualFields } from '@/common/utils/resolveClientDataActualFields'
import { setType } from '@/common/utils/typedValueSerializer'
import { resolveTreeTableFieldNameFromPageFields } from '@/common/utils/treeTableSearch'
import type { ActionContext, ActionDependencies } from '@/services/action/types'
import {
  branchSetsTreeTableNextSearchFlag,
  branchSetsTreeTableSearchFlag,
  findTabRoutedSearchBranch,
  isTabRoutedSearchAction,
  type TabRoutedSearchBranch,
} from '@/services/action/parseTabRoutedSearchOnClick'
import {
  getActionOnClickScript,
  resolveTabRouteFieldFromContext,
  resolveTabRouteValue,
  resolveTreeTableNameForTabRouteBranch,
} from '@/services/action/resolveTabRouteValue'

export {
  isTabRoutedSearchAction,
  findTabRoutedSearchBranch,
  branchSetsTreeTableSearchFlag,
  parseTabRoutedSearchBranches,
  extractTabRouteFieldFromScript,
} from '@/services/action/parseTabRoutedSearchOnClick'
export {
  getActionOnClickScript,
  getFocusedPageId,
  readStringField,
  resolveTabRouteFieldFromContext,
  resolveTabRouteValue,
  resolveTreeTableNameForTabRouteBranch,
} from '@/services/action/resolveTabRouteValue'

function readFieldEntry(pageFields: Record<string, unknown> | undefined, fieldName: string): unknown {
  if (!pageFields) return undefined
  const entry = pageFields[fieldName]
  if (entry == null) return undefined
  if (typeof entry === 'object' && entry !== null && 'value' in entry) {
    return (entry as { value: unknown }).value
  }
  return entry
}

function resolveElementFieldNameByRef(
  elementRef: string,
  dependencies?: ActionDependencies,
): string {
  const getElements = dependencies?.formProvider?.getElements
  if (typeof getElements !== 'function') return ''
  const elements = getElements()
  for (const el of Object.values(elements)) {
    if (el == null || typeof el !== 'object') continue
    const rec = el as { elementId?: string | number; fieldName?: string; name?: string }
    if (String(rec.elementId ?? '') === elementRef) {
      return String(rec.fieldName ?? rec.name ?? '').trim()
    }
  }
  return ''
}

function resolveDynamicFieldName(
  token: string,
  dependencies: ActionDependencies | undefined,
  branch?: TabRoutedSearchBranch,
): string {
  const m = token.match(/^__element__:(.+):(.+)$/)
  if (!m) return token
  const elementRef = String(m[1] ?? '').trim()
  const suffix = m[2] ?? ''
  let base = resolveElementFieldNameByRef(elementRef, dependencies)
  if (!base && branch?.reloadElementId && String(branch.reloadElementId) === elementRef) {
    base = resolveElementFieldNameByRef(branch.reloadElementId, dependencies)
  }
  return base ? `${base}${suffix}` : token
}

function normalizeSearchString(value: unknown, kind: TabRoutedSearchBranch['setFields'][number]['valueKind']): string {
  let s = String(value ?? '')
  if (kind === 'searchStringTrim' || kind === 'searchStringNormalizeSpaces') {
    s = s.trim()
  }
  if (kind === 'searchStringNormalizeSpaces') {
    s = s.replace(/\s+/g, ' ')
  }
  return s
}

export function readSearchStringValue(raw: unknown): string {
  if (raw == null) return ''
  if (typeof raw === 'string') return raw.trim()
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = (raw as { value?: unknown }).value
    return v == null ? '' : String(v).trim()
  }
  return String(raw).trim()
}

export function resolveActionSearchString(
  context: ActionContext,
  dependencies: ActionDependencies | undefined,
  pageFields: Record<string, unknown>,
): string {
  if (context.middleScriptUniData) {
    const middleFields = resolveClientDataActualFields(context.middleScriptUniData)
    if (Object.prototype.hasOwnProperty.call(middleFields, 'searchString')) {
      return readSearchStringValue(middleFields.searchString)
    }
  }
  const collectLive = dependencies?.formProvider?.collectLiveFieldOverrides
  if (typeof collectLive === 'function' && context.formId != null) {
    const live = readSearchStringValue(collectLive(context.formId)?.searchString?.value)
    if (live) return live
  }
  const fromPage = readSearchStringValue(readFieldEntry(pageFields, 'searchString'))
  if (fromPage) return fromPage
  return ''
}

function buildSearchBranchClientFields(
  branch: TabRoutedSearchBranch,
  searchString: unknown,
  dependencies: ActionDependencies | undefined,
): Record<string, { value: unknown; type: string }> {
  const out: Record<string, { value: unknown; type: string }> = {}
  for (const field of branch.setFields) {
    const fieldName = resolveDynamicFieldName(field.fieldName, dependencies, branch)
    if (fieldName.startsWith('__element__:')) continue

    let value: unknown = field.value
    if (field.valueKind === 'searchStringTrim' || field.valueKind === 'searchStringNormalizeSpaces') {
      value = normalizeSearchString(searchString, field.valueKind)
    }

    const type =
      typeof value === 'boolean'
        ? 'Boolean'
        : typeof value === 'number'
          ? Number.isInteger(value)
            ? 'Long'
            : 'Double'
          : 'String'
    out[fieldName] = { value, type }
  }
  return out
}

function applyResolvedSearchStringToPayload(
  payload: Record<string, unknown>,
  searchString: unknown,
): void {
  const resolved = readSearchStringValue(searchString)
  if (!resolved) return
  payload.searchString = resolved
}

function applyResolvedSearchStringToClientDataFields(
  clientDataFields: Record<string, { value: unknown; type: string }>,
  searchString: unknown,
  options?: { overwriteExisting?: boolean },
): void {
  const resolved = readSearchStringValue(searchString)
  if (options?.overwriteExisting) {
    clientDataFields.searchString = { value: resolved, type: 'String' }
    return
  }
  if (!resolved || clientDataFields.searchString) return
  clientDataFields.searchString = { value: resolved, type: 'String' }
}

export function buildTabRoutedTreeTableSearchReloadPayload(
  branch: TabRoutedSearchBranch,
  tableName: string,
  searchString: unknown,
  dependencies: ActionDependencies | undefined,
  routeField?: string,
  routeValue?: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  if (routeField && routeValue) {
    payload[routeField] = routeValue
  }
  const branchFields = buildSearchBranchClientFields(branch, searchString, dependencies)
  for (const [key, field] of Object.entries(branchFields)) {
    payload[key] = field.value
  }
  applyResolvedSearchStringToPayload(payload, searchString)
  applyFreshTreeTableSearchReloadOverrides(payload, tableName, branch)
  delete payload[tableName]
  return payload
}

export function applyFreshTreeTableSearchReloadOverrides(
  reloadPayload: Record<string, unknown>,
  tableName: string,
  branch: TabRoutedSearchBranch | null,
): void {
  if (!branch || !branchSetsTreeTableSearchFlag(branch) || branchSetsTreeTableNextSearchFlag(branch)) {
    return
  }
  reloadPayload[`${tableName}${IKey.SHOW_MESSAGE_DIALOG}`] = false
  reloadPayload[`${tableName}${IKey.TT_NEXT_SEARCH_POSITION}`] = 0
  reloadPayload[`${tableName}__prevSearchPosition`] = 0
  reloadPayload[`${tableName}__direction`] = 0
  reloadPayload[`${tableName}${IKey.SELECTED}`] = false
  reloadPayload[`${tableName}__selected`] = false
  reloadPayload[`${tableName}${IKey.TT_FORCE_LOAD_ALL}`] = false
  reloadPayload[`${tableName}${IKey.TT_INIT}`] = false
  reloadPayload[`${tableName}__${IKey.ROW_NUM}`] = -1
  reloadPayload[`${tableName}__${IKey.ROW_CHECKED}`] = false
  delete reloadPayload[`${tableName}${IKey.PARAMS}`]
  delete reloadPayload[`${tableName}${IKey.TT_COUNT_CHILDREN}`]
}

function clientDataFieldsToResolutionMap(
  clientDataFields: Record<string, { value: unknown; type: string }>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(clientDataFields)) {
    out[key] = field
  }
  return out
}

function readTruthyFieldValue(raw: unknown): boolean | null {
  if (raw === true || raw === false) return raw
  if (raw === 'true' || raw === 1 || raw === '1') return true
  if (raw === 'false' || raw === 0 || raw === '0') return false
  if (raw != null && typeof raw === 'object' && 'value' in raw) {
    return readTruthyFieldValue((raw as { value?: unknown }).value)
  }
  return null
}

function collectSearchTargetTableNames(
  script: string,
  routeValue: string | null,
  clientDataFields: Record<string, { value: unknown; type: string }>,
  pageFields: Record<string, unknown>,
  branch: TabRoutedSearchBranch,
  dependencies: ActionDependencies | undefined,
): string[] {
  const names = new Set<string>()
  if (routeValue) {
    const fromBranch = resolveTreeTableNameForTabRouteBranch(script, routeValue, dependencies)
    if (fromBranch) names.add(fromBranch)
  }

  const flat: Record<string, unknown> = { ...pageFields, ...clientDataFieldsToResolutionMap(clientDataFields) }
  for (const key of Object.keys(flat)) {
    if (!key.endsWith(IKey.TT_SEARCH)) continue
    if (readTruthyFieldValue(flat[key]) !== true) continue
    const tableName = key.slice(0, -IKey.TT_SEARCH.length)
    if (tableName) names.add(tableName)
  }

  if (names.size === 0 && branchSetsTreeTableSearchFlag(branch)) {
    const inferred = resolveTreeTableFieldNameFromPageFields(flat)
    if (inferred) names.add(inferred)
  }

  return [...names]
}

function writeFlatPayloadToClientDataFields(
  clientDataFields: Record<string, { value: unknown; type: string }>,
  flatPayload: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(flatPayload)) {
    const type =
      typeof value === 'boolean'
        ? 'Boolean'
        : typeof value === 'number'
          ? Number.isInteger(value)
            ? 'Long'
            : 'Double'
          : 'String'
    clientDataFields[key] = { value, type }
  }
}

export function ensureTabRoutedSearchClientFields(
  clientDataFields: Record<string, { value: unknown; type: string }>,
  context: ActionContext,
  dependencies: ActionDependencies | undefined,
  pageFields: Record<string, unknown>,
  pageData: Record<string, unknown> | null | undefined,
): void {
  const script = getActionOnClickScript(context)
  if (!isTabRoutedSearchAction(script)) return

  const routeField = resolveTabRouteFieldFromContext(context)
  if (!routeField) return

  const routeValue = resolveTabRouteValue(
    pageFields,
    context,
    pageData,
    clientDataFieldsToResolutionMap(clientDataFields),
    dependencies,
  )
  if (routeValue) {
    clientDataFields[routeField] = { value: routeValue, type: 'String' }
  }

  const branch = findTabRoutedSearchBranch(script, routeValue)
  if (!branch) return

  const middleFields = context.middleScriptUniData
    ? resolveClientDataActualFields(context.middleScriptUniData)
    : null
  const searchString = resolveActionSearchString(context, dependencies, pageFields)
  applyResolvedSearchStringToClientDataFields(clientDataFields, searchString, {
    overwriteExisting: Boolean(
      middleFields && Object.prototype.hasOwnProperty.call(middleFields, 'searchString'),
    ),
  })
  const branchFields = buildSearchBranchClientFields(branch, searchString, dependencies)
  for (const [key, field] of Object.entries(branchFields)) {
    clientDataFields[key] = field
  }

  if (!branchSetsTreeTableSearchFlag(branch) || branchSetsTreeTableNextSearchFlag(branch)) {
    return
  }

  const tableNames = collectSearchTargetTableNames(
    script,
    routeValue,
    clientDataFields,
    pageFields,
    branch,
    dependencies,
  )
  if (!tableNames.length) return

  const flatPayload: Record<string, unknown> = {}
  for (const [key, field] of Object.entries(clientDataFields)) {
    flatPayload[key] = field.value
  }

  for (const tableName of tableNames) {
    applyFreshTreeTableSearchReloadOverrides(flatPayload, tableName, branch)
    delete clientDataFields[tableName]
    delete flatPayload[tableName]
  }

  writeFlatPayloadToClientDataFields(clientDataFields, flatPayload)
}
